import {
  Box,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material"
import { ClipboardList, Search } from "lucide-react"
import { useCallback, useEffect, useRef, useState, type JSX } from "react"
import { useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/ui/empty-state"
import { Pagination, paginationPropsFromMeta } from "@/components/ui/pagination"
import { Money } from "@crm-src/components/money"
import { ResellerOnly, useResellerAccess } from "@crm-src/components/reseller"
import {
  purchaseOrdersApi,
  type PurchaseOrder,
  type PurchaseOrderListParams,
  type PurchaseOrderPagination,
  type PurchaseOrderSort,
  type PurchaseOrderSource,
  type PurchaseOrderStatus,
} from "@crm-src/lib/purchase-orders-api"

import { PurchaseOrderSourceChip, PurchaseOrderStatusChip } from "./purchase-order-chips"
import {
  centsToDollars,
  formatPurchaseOrderDate,
  PURCHASE_ORDER_SOURCE_LABELS,
  PURCHASE_ORDER_STATUS_LABELS,
} from "./purchase-order-format"

/** Rows per page. Matches every other CRM list. */
export const PURCHASE_ORDERS_PER_PAGE = 25
/** How long after the last keystroke the search is sent. */
export const PURCHASE_ORDER_SEARCH_DEBOUNCE_MS = 400

export interface PurchaseOrdersListProps {
  /** Narrow to one customer (the customer profile's tab). The Customer column is dropped. */
  customerId?: string | number | undefined
  /** Narrow to one reseller (the reseller profile's tab). The Reseller column is dropped. */
  resellerId?: string | number | undefined
  /** Bump to re-read the current page (after an upload elsewhere on the page). */
  refreshKey?: number
  emptyMessage?: string
  "data-cy"?: string
}

const DASH = <Typography component="span" sx={{ fontSize: 14, color: "text.disabled" }}>—</Typography>

/**
 * ONE purchase-order table — the Purchase Orders page and the Purchase Orders
 * tabs on the customer and reseller profiles all render this, differing only
 * in the `customerId` / `resellerId` filter that is sent to crm-api.
 *
 * Search, the Status and Source filters, the sort and the page are all asked
 * of the SERVER (crm-api scopes the rows to the viewer; a client-side filter
 * would filter a page, not the list).
 *
 * The walls: every figure goes through `<Money>` (and a total nobody entered
 * shows "—", not "$0.00"), and the Reseller column exists only for a viewer
 * with reseller access — the header through `useResellerAccess`, the cell
 * through `<ResellerOnly>`. crm-api also strips both server-side.
 */
export function PurchaseOrdersList({
  customerId,
  resellerId,
  refreshKey = 0,
  emptyMessage = "No purchase orders yet.",
  "data-cy": dataCy = "purchase-orders-list",
}: PurchaseOrdersListProps): JSX.Element {
  const navigate = useNavigate()
  const resellerAccess = useResellerAccess()
  const showCustomer = customerId === undefined
  const showReseller = resellerId === undefined && resellerAccess

  const [input, setInput] = useState("")
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState<PurchaseOrderStatus | "">("")
  const [source, setSource] = useState<PurchaseOrderSource | "">("")
  const [sort, setSort] = useState<PurchaseOrderSort>("created_at")
  const [dir, setDir] = useState<"asc" | "desc">("desc")
  const [page, setPage] = useState(1)

  const [rows, setRows] = useState<PurchaseOrder[]>([])
  const [pagination, setPagination] = useState<PurchaseOrderPagination | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Debounce the text box only; a filter, a header or a page click fires at once.
  useEffect(() => {
    const next = input.trim()
    if (next === search) return
    const handle = setTimeout(() => {
      setSearch(next)
      // A new term means page 1 again.
      setPage(1)
    }, PURCHASE_ORDER_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [input, search])

  // Drop a slow earlier response that lands after a faster later one.
  const requestSeq = useRef(0)

  const load = useCallback(async () => {
    const seq = ++requestSeq.current
    const params: PurchaseOrderListParams = {
      page,
      per_page: PURCHASE_ORDERS_PER_PAGE,
      sort,
      dir,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
      ...(source ? { source } : {}),
      ...(customerId !== undefined ? { customer_id: customerId } : {}),
      ...(resellerId !== undefined ? { reseller_id: resellerId } : {}),
    }
    setLoading(true)
    try {
      const response = await purchaseOrdersApi.list(params)
      if (seq !== requestSeq.current) return
      setRows(response.purchase_orders ?? [])
      setPagination(response.meta?.pagination ?? null)
      setError(null)
    } catch (err: any) {
      if (seq !== requestSeq.current) return
      setRows([])
      setError(err?.response?.data?.error || "The purchase orders could not be loaded.")
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [page, sort, dir, search, status, source, customerId, resellerId])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const toggleSort = (column: PurchaseOrderSort) => {
    if (sort === column) {
      setDir(dir === "asc" ? "desc" : "asc")
    } else {
      setSort(column)
      // Newest / largest first is what anyone opening a column wants.
      setDir(column === "number" ? "asc" : "desc")
    }
    setPage(1)
  }

  const sortHeader = (column: PurchaseOrderSort, label: string, align: "left" | "right" = "left") => (
    <TableCell align={align} sortDirection={sort === column ? dir : false}>
      <TableSortLabel
        active={sort === column}
        direction={sort === column ? dir : "asc"}
        onClick={() => toggleSort(column)}
        data-cy={`po-sort-${column}`}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  )

  const filtered = !!(search || status || source)
  const columnCount = 6 + (showCustomer ? 1 : 0) + (showReseller ? 1 : 0)

  return (
    <Card data-cy={dataCy}>
      <CardContent sx={{ display: "grid", gap: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search PO number, buyer, customer..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            inputProps={{ "data-cy": "po-search", "aria-label": "Search purchase orders" }}
            sx={{ width: { xs: "100%", sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: "flex", color: "text.disabled" }}>
                    <Search size={16} />
                  </Box>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as PurchaseOrderStatus | "")
              setPage(1)
            }}
            sx={{ minWidth: 150 }}
            SelectProps={{ SelectDisplayProps: { "data-cy": "po-filter-status" } as any }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {(Object.keys(PURCHASE_ORDER_STATUS_LABELS) as PurchaseOrderStatus[]).map((key) => (
              <MenuItem key={key} value={key}>
                {PURCHASE_ORDER_STATUS_LABELS[key]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Source"
            value={source}
            onChange={(e) => {
              setSource(e.target.value as PurchaseOrderSource | "")
              setPage(1)
            }}
            sx={{ minWidth: 170 }}
            SelectProps={{ SelectDisplayProps: { "data-cy": "po-filter-source" } as any }}
          >
            <MenuItem value="">All sources</MenuItem>
            {(Object.keys(PURCHASE_ORDER_SOURCE_LABELS) as PurchaseOrderSource[]).map((key) => (
              <MenuItem key={key} value={key}>
                {PURCHASE_ORDER_SOURCE_LABELS[key]}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {sortHeader("number", "PO record")}
                <TableCell>Buyer</TableCell>
                {showCustomer && <TableCell>Customer</TableCell>}
                {showReseller && <TableCell>Reseller</TableCell>}
                {sortHeader("order_date", "Order date")}
                {sortHeader("total_cents", "Total", "right")}
                <TableCell>Source</TableCell>
                {sortHeader("status", "Status")}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((po) => (
                <TableRow
                  key={po.id}
                  hover
                  data-cy="po-row"
                  onClick={() => navigate(`/crm/purchase-orders/${po.id}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}>{po.number}</Typography>
                    {po.po_number && (
                      <Typography sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                        Buyer PO {po.po_number}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{po.buyer?.name || DASH}</TableCell>
                  {showCustomer && <TableCell sx={{ maxWidth: 220 }}>{po.customer?.company_name || DASH}</TableCell>}
                  {showReseller && (
                    <TableCell sx={{ maxWidth: 200 }} data-cy="po-reseller">
                      <ResellerOnly fallback={DASH}>{po.reseller?.name || DASH}</ResellerOnly>
                    </TableCell>
                  )}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatPurchaseOrderDate(po.order_date)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>
                    {po.total_cents === null || po.total_cents === undefined ? DASH : <Money value={centsToDollars(po.total_cents)} />}
                  </TableCell>
                  <TableCell>
                    <PurchaseOrderSourceChip source={po.source} />
                  </TableCell>
                  <TableCell>
                    <PurchaseOrderStatusChip status={po.status} />
                  </TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ border: 0, pt: 2 }}>
                    <EmptyState
                      icon={<ClipboardList size={20} />}
                      title={error ? "Couldn't load purchase orders" : filtered ? "No purchase order matches" : emptyMessage}
                      {...(error ? { description: error } : filtered ? { description: "Try a different search or filter." } : {})}
                    />
                  </TableCell>
                </TableRow>
              )}
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ color: "text.secondary" }}>
                    Loading purchase orders...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Pagination
          {...paginationPropsFromMeta(pagination)}
          entityNoun="purchase orders"
          hideIfSinglePage
          onPageChange={setPage}
        />
      </CardContent>
    </Card>
  )
}
